import type { StoreLink } from './store-links';

export const STUDIO_NAME = 'GOOD BUILD';

export const ROOM_BACKDROP: string | null = null;

/**
 * Where a game is, as far as somebody deciding whether to play it is concerned.
 *
 * `early-access` is its own state rather than a flavour of `released`, because
 * the two answer differently the only question the badge is asked: *can I play
 * this, and is it finished?* Calling an early-access game `Out now` is true
 * about the first half and quietly wrong about the second, and it is the half a
 * player is owed before they pay.
 */
export type GameStatus = 'released' | 'early-access' | 'coming-soon' | 'in-development';

export interface GameArt {
  /**
   * The front of the case, 600×900. The one that matters most.
   *
   * It is what the pile on the front page shows before anybody has opened
   * anything, and it is the one slot with no sensible fallback: the case panel
   * takes the texture as it comes, so a landscape image in this slot is
   * stretched to portrait rather than cropped to it. Null draws the plate with
   * the game's name on it, which is the honest empty state.
   */
  cover: string | null;
  /**
   * What the disc in the open case is printed with. Square, transparent.
   *
   * Square, and there is no fallback for the same reason `cover` has none: the
   * disc is a circle and the texture goes on unfitted, so a landscape logo in
   * this slot is a squashed logo. Null draws the disc in the panel's accent,
   * which is a state `Disc` was written for.
   *
   * Steam has no square asset in its library set, so a title imported from
   * there arrives with this empty and looks right.
   */
  disc: string | null;
  /** The landscape card, 616×353. Steam's main capsule fits exactly. */
  capsule: string | null;
  /** The full-bleed banner at the top of the game's own page. */
  hero: string | null;
  /** The game's own wordmark, transparent. */
  logo: string | null;
  /** Behind the game's page, at a quarter opacity. */
  background: string | null;
}

export interface GameMedia {
  id: string;
  kind: 'screenshot' | 'trailer';
  url: string;
  posterUrl: string | null;
}

export interface Game {
  slug: string;
  name: string;
  tagline: string;
  status: GameStatus;
  releaseLine: string;
  platforms: readonly string[];
  /**
   * How you play it: solo, co-op, and the rest. Steam's `categories`.
   *
   * Filtered to the ones that describe playing the game. Steam mixes storefront
   * features into that list — Family Sharing, Steam Cloud, trading cards — and
   * they are facts about Steam rather than about the game, so they do not
   * belong on a case that also links to Epic.
   */
  modes: readonly string[];
  /** What kind of game it is. Steam's `genres`. */
  genres: readonly string[];
  description: readonly string[];
  features: readonly string[];
  art: GameArt;
  media: readonly GameMedia[];
  links: readonly StoreLink[];
}

function shots(slug: string, count: number): GameMedia[] {
  return Array.from({ length: count }, (_, index) => ({
    id: `shot-${index + 1}`,
    kind: 'screenshot' as const,
    url: `/games/${slug}/shot-${index + 1}.jpg`,
    posterUrl: null,
  }));
}

export const GAMES: readonly Game[] = [
  {
    slug: 'victor-space-program',
    name: 'Victor Space Program',
    tagline: 'Land on a strange planet. Dig up what you need. Build the rocket home.',
    status: 'early-access',
    releaseLine: 'Out now in early access',
    platforms: ['Windows'],
    modes: ['Single-player', 'Multi-player', 'Co-op', 'Online Co-op'],
    genres: ['Indie', 'RPG', 'Early Access'],
    description: [
      'You land on a planet nobody has been to before. Everything you need is in the ground under you. Dig it up, build what you need from it, and put together a rocket to get home.',
      'Every planet is made fresh from a seed, so the land, the plants, the creatures, the sky and the music are different every time you start. Bring friends, or go alone.',
    ],
    features: [
      'Dig and build. Every block can be mined or placed.',
      'Survive. Hunt, cook, and eat, or the planet wins.',
      'Craft your way up, from bare hands to a working workshop.',
      'Watch out at night. Some things only come out then.',
      'Build the rocket. The parts are spread wide and deep.',
    ],
    art: {
      cover: '/games/victor-space-program/cover.jpg',
      disc: null,
      capsule: '/games/victor-space-program/capsule.jpg',
      hero: '/games/victor-space-program/hero.jpg',
      logo: '/games/victor-space-program/logo.png',
      background: '/games/victor-space-program/shot-4.jpg',
    },
    media: shots('victor-space-program', 6),
    links: [
      {
        platform: 'steam',
        url: 'https://store.steampowered.com/app/4048540/Victor_Space_Program/',
      },
      {
        platform: 'epic',
        url: 'https://store.epicgames.com/p/victor-space-program-18fb6b',
      },
    ],
  },
  {
    slug: 'poly-plaza-ultimate',
    name: 'Poly Plaza ULTIMATE',
    tagline: 'An open world idle tycoon about buying up a city.',
    status: 'released',
    releaseLine: 'Out now, 28 August 2025',
    platforms: ['Windows'],
    modes: ['Single-player'],
    genres: ['Indie', 'RPG', 'Simulation'],
    description: [
      'Poly Plaza ULTIMATE is an idle tycoon RPG. You explore an open city, buy the properties in it, and they go on paying you whether you are there or not.',
      'What you earn buys the next place, and the next place pays for a vehicle, and the vehicle lets you carry more of what you find. The city opens up as you go.',
    ],
    features: [
      'Buy properties and earn from them while you are away.',
      'Explore an open city for things worth selling.',
      'Craft the expensive things, for cash and for levels.',
      'Buy a vehicle to get around and carry more.',
    ],
    art: {
      cover: '/games/poly-plaza-ultimate/cover.jpg',
      disc: null,
      capsule: '/games/poly-plaza-ultimate/capsule.jpg',
      hero: '/games/poly-plaza-ultimate/hero.jpg',
      logo: '/games/poly-plaza-ultimate/logo.png',
      background: '/games/poly-plaza-ultimate/shot-3.jpg',
    },
    media: shots('poly-plaza-ultimate', 6),
    links: [
      {
        platform: 'steam',
        url: 'https://store.steampowered.com/app/3208170/Poly_Plaza_ULTIMATE/',
      },
      {
        platform: 'epic',
        url: 'https://store.epicgames.com/p/poly-plaza-ultimate-26e1a2',
      },
    ],
  },
];

export function gameBySlug(slug: string): Game | null {
  return GAMES.find((game) => game.slug === slug) ?? null;
}

export function statusLabel(game: Game): string {
  if (game.status === 'released') return 'Out now';
  if (game.status === 'early-access') return 'Early access';
  if (game.status === 'coming-soon') return 'Coming soon';
  return 'In development';
}

/**
 * How you play it, as the three answers anybody is actually asking for.
 *
 * ── Three badges, not the whole category list ────────────────────────────
 *
 * Steam hands over four or five strings — `Single-player`, `Multi-player`,
 * `Co-op`, `Online Co-op` — and printing them verbatim gives a case a row of
 * eight chips in which the two that matter are indistinguishable from the six
 * that do not. Somebody looking at a game is asking whether they can play it
 * alone, with a friend, or with a crowd. That is three questions, so it is three
 * badges.
 *
 * Matched on the shape of the string rather than on Steam's category ids,
 * because the ids are Steam's and the other shops describe the same facts in
 * their own words. A game imported from anywhere with `Co-op` in its modes
 * comes out with a co-op badge.
 *
 * Order is solo, co-op, multiplayer — fewest people to most, which is the order
 * somebody reads them in.
 */
export interface PlayBadge {
  label: string;
  /** A name from the icon sprite. */
  icon: string;
}

const PLAY_BADGES: readonly { match: RegExp; badge: PlayBadge }[] = [
  { match: /single[\s-]?player|solo/i, badge: { label: 'Solo', icon: 'user' } },
  { match: /co[\s-]?op/i, badge: { label: 'Co-op', icon: 'users' } },
  { match: /multi[\s-]?player/i, badge: { label: 'Multiplayer', icon: 'globe' } },
];

export function playModes(game: Game): PlayBadge[] {
  return PLAY_BADGES.filter(({ match }) => game.modes.some((mode) => match.test(mode))).map(
    ({ badge }) => badge,
  );
}

export function galleryItems(game: Game): GameMedia[] {
  return [...game.media].sort((left, right) => {
    if (left.kind === right.kind) return 0;
    return left.kind === 'trailer' ? -1 : 1;
  });
}
